import { ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { ClubJudgeApi, JudgesForClubGridDto, JudgesForClubSearch } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { AddButton, CustomDataTable, DeleteButton, EmailButton, ExportButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useDeleteApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { ButtonGroup, Checkbox, Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { ClubJudgesPanelStack } from "./clubJudges/ClubJudgesPanelStack";

export interface IClubJuryPageProps {
  clubId: number;
  editMode: boolean;
}

export const ClubJuryPage: React.FunctionComponent<IClubJuryPageProps> = ({ clubId, editMode }) => {
  const api = useApiService(ClubJudgeApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sObjState, setsobjState] = React.useState<any>();

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { lastName: "ASC" },
  });

  const { totalCount } = tableState;

  const searchFunction = React.useCallback(
    (sObj: JudgesForClubSearch) => {
      const sobj = JudgesForClubSearch.fromJS({
        ...sObj,
        clubId: clubId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, clubId, currentLanguage.languageId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.deleteJudgeForClub(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: JudgesForClubGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.judgesForClubId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.LastName),
        fieldName: "lastName",
        render: (row: JudgesForClubGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.computedName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Language),
        fieldName: "language",
      },
      {
        header: () => t(ETLCodes.Email),
        fieldName: "email",
        render: (row: JudgesForClubGridDto) => <EmailButton email={row.email} />,
      },
      {
        header: () => t(ETLCodes.PhoneNumber),
        fieldName: "phoneNumber",
      },
      {
        header: () => t(ETLCodes.MobilePhoneNumber),
        fieldName: "mobilePhoneNumber",
      },
      {
        header: () => t(ETLCodes.IsAptForBeauty),
        fieldName: "isAptForBeauty",
        render: (row: JudgesForClubGridDto) => <Checkbox disabled checked={row.isAptForBeauty} />,
      },
      {
        header: () => t(ETLCodes.IsAptForWork),
        fieldName: "isAptForWork",
        render: (row: JudgesForClubGridDto) => <Checkbox disabled checked={row.isAptForWork} />,
      },
    ],
    [deleteItem, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <FieldSet
      title={t(ETLCodes.Judges, { totalCount: totalCount })}
      rightElement={
        <ButtonGroup>
          <AddButton
            onClick={(e) => {
              e.stopPropagation();
              addItemFunction();
            }}
            text={t(ETLCodes.General_Add)}
            intent={Intent.PRIMARY}
          />
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonGroup>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />

      <Dialog
        title={t(ETLCodes.Jury)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 750, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <ClubJudgesPanelStack
            clubId={clubId}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </FieldSet>
  );
};
